import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useLocation } from 'react-router-dom';
import Joyride from 'react-joyride';
import styled from 'styled-components';
import _ from 'lodash';
import companyFeatureFlags from '@Helpers/featureFlags';

import { Box, Text } from 'grommet';

import { AppButton, AppAnchor } from '@Components/Control';
import { endTourRequest } from '@Actions';

const StyledText = styled(Text)`
  color: var(--page-modal-text);
  text-align: left;
  margin: 0;
  padding: 0;
  font-family: 'Geist', 'Roboto', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  white-space: pre-line;
  line-height: 1.25;
`;

const renderTitle = (title) => (
  <Box background="var(--page-modal-bg)" pad="0.5rem">
    <StyledText size="1.25rem" weight={600} textAlign="start">
      {title}
    </StyledText>
  </Box>
);

const renderContent = (message) => (
  <Box background="var(--page-modal-bg)">
    <StyledText size="1rem" weight={400} textAlign="start">
      {message}
    </StyledText>
  </Box>
);

const GuidedTour = ({
  showTour,
  endTour,
  companyFeatures = null,
}) => {
  const { pathname } = useLocation();
  const pathParts = pathname.split('/').filter(Boolean);
  const isManualClaimDetailPage = pathParts.length === 3
    && pathParts[0] === 'content-id'
    && pathParts[1] === 'manual-claims'
    && pathParts[2] !== 'submit';

  const [wideNav, setWideNav] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 48.0625em)').matches,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 48.0625em)');
    const onChange = () => setWideNav(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const handleEndTour = () => endTour();

  // Use featureFlags util for all feature checks
  const flags = companyFeatureFlags(companyFeatures) || {};
  const {
    searchEnabled,
    reviewEnabled,
    overviewEnabled,
    settingsEnabled,
    reportsEnabled,
    contentIdEnabled,
    contentIdAssetUploadEnabled,
    fingerprintingEnabled,
  } = flags;

  // Settings sub-features
  const authorListsEnabled = settingsEnabled && _.get(flags, 'settingsConfig.authorListsAccess');
  const domainListsEnabled = settingsEnabled && _.get(flags, 'settingsConfig.dmcaDomainListsAccess');
  const reportsConfig = companyFeatures && _.find(companyFeatures, { key: 'reports_config' });
  const reportsKeys = reportsConfig?.config && reportsConfig?.enabled
    && Object.keys(reportsConfig?.config);
  const filteredReportsKeys = reportsKeys
    && reportsKeys.filter((key) => !_.isEmpty(reportsConfig.config[key]));
  const reportsActuallyEnabled = (reportsConfig?.enabled && filteredReportsKeys?.length > 0);

  const steps = [
    {
      target: '.tour-app-container',
      title: renderTitle('Welcome to the Redflag platform!'),
      content: renderContent('Take a quick tour of your dashboard to learn about the key features and get started. Click next to continue.'),
      placement: 'center',
    },
    ...(searchEnabled
      ? [
        {
          target: '.tour-new-search',
          title: renderTitle('Create Searches'),
          content: renderContent('Create searches for your titles so our platform can start to find infringing content. It typically takes a day or two for us to find results, depending on platform and other search parameters.'),
        },
        {
          target: '.tour-all-infringements',
          title: renderTitle('View Infringements'),
          content: renderContent('View and respond to infringing content found during the search process. You can send DMCA notices, track infringements, and check the status of infringements here. \n\n You can check the Confirmed Removals page to track infringements that have been confirmed down by our system.'),
        },
        {
          target: '.tour-all-searches',
          title: renderTitle('Track Searches'),
          content: renderContent('Here you can view and manage search progress and filter by the current status of your searches. You can pause or resume active searches, find search details including found infringements, and review those infringements by checking the search details.'),
        },
        {
          target: '.tour-upload-results',
          title: renderTitle('Upload Results'),
          content: renderContent('Have you found infringing content elsewhere that you would like to send DMCA notices for using our system? \n\nYou can upload information about the infringing content using the Upload Results page to get the DMCA process started and have it taken down.'),
        },
      ] : []),
    ...(reviewEnabled
      ? [
        {
          target: '.tour-review',
          title: renderTitle('Review Tool'),
          content: renderContent('Potentially infringing content found through your searches can be found in the Review Tool. \n\nYou can view the content, select a review status, submit your review, and then our system will take the appropriate action based on your selections.'),
        },
      ] : []),
    ...(authorListsEnabled
      ? [
        {
          target: '.tour-dmca-author_allowlist',
          title: renderTitle('DMCA Author Allowlist'),
          content: renderContent('If you have other authorized channels, you can add them to your allowlist here to ensure that no DMCA notices will be sent for authorized content.'),
        },
      ] : []),
    ...(domainListsEnabled
      ? [
        {
          target: '.tour-dmca-allowlist',
          title: renderTitle('DMCA Deny and Allow lists'),
          content: renderContent('The DMCA Denylist allows you to add domains that are known to hold infringing content to avoid the need for manual review. \n\nThe DMCA Allowlist is for any authorized domains; no notices will be sent for any domain that matches one you have added to this list'),
        },
      ] : []),
    ...(overviewEnabled
      ? [
        {
          target: '.tour-piracy',
          title: renderTitle('Piracy Overview'),
          content: renderContent('The Piracy Overview page provides a summary of your searches and results. You can filter by date range, search type, and more.'),
        },
      ] : []),
    ...(reportsActuallyEnabled || reportsEnabled
      ? [
        {
          target: '.tour-reports',
          title: renderTitle('Reporting'),
          content: renderContent('Find detailed analytics reports and summaries of searches and results here.'),
        },
      ] : []),
    // --- Content ID Features ---
    ...(contentIdEnabled
      ? [
        {
          target: '.tour-content-id-assets',
          title: renderTitle('Manage Assets'),
          content: renderContent('View and manage all your YouTube Content ID assets and claims on the "Manage assets" page. You can see details about each asset, as well as update your asset metadata and select new Asset Match Policies. \n\nYou can also view all claims associated with your assets, and track each claims\' status and details.'),
        },
        {
          target: '.tour-content-id-claims',
          title: renderTitle('Manage Claims'),
          content: renderContent('You can view and manage all your YouTube Content ID claims on the "Manage claims" page. Click the Video Details column to see information such as the claimed video link and the channel owner. \n\nFor deeper insights, use the See Details button to open the Claim Details and Claim History.'),
        },
        {
          target: isManualClaimDetailPage
            ? '.tour-manual-claim-request-detail'
            : '.tour-content-id-claims',
          title: renderTitle('Manual claim requests'),
          content: renderContent(
            isManualClaimDetailPage
              ? 'This page shows your batch request status, a summary of claim line items, and each claim\'s status. Use More Details for match type, timing, channel, and YouTube claim ID; use View Notes when a note exists. Go back returns to your requests list.'
              : 'On Manage claims, open the Manual claim requests tab to submit pasted rows or a CSV, then track requests in the table. Open any request to review line items, statuses, and details for each claim.',
          ),
        },
        ...(contentIdAssetUploadEnabled ? [
          {
            target: '.tour-content-id-asset-upload',
            title: renderTitle('Upload Assets'),
            content: renderContent('If you don\'t have existing assets, you can upload new assets using the "Upload assets" page. Videos can be uploaded in bulk, and can optionally include a CSV file to set metadata and Asset Match Policies during upload. \n\nYou can also track the status of all your upload requests and their associated files by clicking into the "View Requests" tab.'),
          },
        ] : []),
        ...(wideNav
          ? [
            {
              target: '.tour-content-id-manual-claims-upload',
              title: renderTitle('Upload claims'),
              content: renderContent(
                'Use "Upload claims" in the top bar to go straight to manual claim submission (paste rows or upload a CSV). You can also open Manage claims → Manual claim requests → Upload Claims in the page banner.',
              ),
            },
          ]
          : []),
        {
          target: '.tour-content-id-allowlist',
          title: renderTitle('Channel Allowlist'),
          content: renderContent('Add or remove YouTube channels from your Content ID allowlist from the "Channel allowlist" page. Channels on this list will be exempt from Content ID claims, so this is a good place to add any extra channels you may own or that you do not want to track claims against.'),
        },
      ] : []),
    ...(fingerprintingEnabled
      ? [
        {
          target: '.tour-fingerprinting-settings',
          title: renderTitle('Fingerprinting settings'),
          content: renderContent('Configure fingerprinting settings to optimize content matching and identification. \n\nUpdates made here will take effect during the next content detection cycle.'),
        },
      ] : []),
    {
      target: '.tour-app-container',
      hideFooter: true,
      title: renderTitle('Thank you from the Redflag team!'),
      content: (
        <Box direction="column" gap="small">
          <StyledText color="var(--page-modal-text)" weight={400} size="1rem" textAlign="start">
            If you have any further questions, please reach out to us at
            {' '}
            <AppAnchor href="mailto:contact@redflagai.co" target="_blank" color="var(--page-alt-highlight)">
              contact@redflagai.co
            </AppAnchor>
          </StyledText>
          <StyledText size="1rem" color="var(--page-modal-text)" weight={400} textAlign="start">
            You can restart this tour at any time from the menu in the top right corner.
          </StyledText>
          <Box direction="column" gap="1rem" align="center" justify="center" width="100%">
            <AppButton
              overrideHover
              onClick={handleEndTour}
              level="tableControls"
              color="var(--button-primary-text)"
              bgColor="var(--button-primary-bg)"
              label="End tour"
            />
          </Box>
        </Box>
      ),
      placement: 'center',
    },
  ];

  if (!showTour) return null;

  return (
    <Box>
      <Joyride
        continuous
        showProgress
        disableScrolling
        disableCloseOnEsc
        hideCloseButton
        showSkipButton
        steps={steps}
        callback={(data) => {
          const { status } = data;

          if (status === 'finished' || status === 'skipped') {
            handleEndTour();
          }
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finished',
          next: 'Next',
          nextLabelWithProgress: 'Next ({step} of {steps})',
          open: 'Open the dialog',
          skip: 'Skip tour',
        }}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: 'var(--page-highlight)',
            textColor: 'var(--page-primary-text)',
            arrowColor: 'var(--page-modal-bg)',
            backgroundColor: 'var(--page-modal-bg)',
            textSize: '1rem',
            textAlign: 'left',
            spotlightPadding: 10,
          },
        }}
      />
    </Box>
  );
};

function mapStateToProps(state) {
  const { companyFeatures } = state.auth;
  const { showTour } = state.notice;

  return {
    showTour,
    companyFeatures,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    endTour: endTourRequest,
  }, dispatch);
}

GuidedTour.propTypes = {
  showTour: PropTypes.bool.isRequired,
  endTour: PropTypes.func.isRequired,
  companyFeatures: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    config: PropTypes.objectOf(PropTypes.any),
  }).isRequired),
};

export default connect(mapStateToProps, mapDispatchToProps)(GuidedTour);
